import { combine, createEffect, createEvent, createStore, sample, split } from 'effector';
import { createGate } from 'effector-react';
import { $data as $enterprises } from '@features/enterprise-select';
import {
  CreateTicketPayload,
  NotifySupportPayload,
  ObjectParts,
  ObjectsResponse,
  RepeatStore,
  Ticket,
  TicketPayload,
  TicketResponse,
  Value,
} from '../../interfaces';
import { getTypesList } from '../../libs';
import { $types } from '../page/page.model';
import { $usersInfo } from '../ticket-assignees/ticket-assignees.model';
import { $rates } from '../ticket-declarant/ticket-declarant.model';
import { $invoices, $marketOrders } from '../ticket-invoice/ticket-invoice.model';
import { $objects } from '../ticket-objects/ticket-objects.model';
import { $repeat } from '../ticket-repeat/ticket-repeat.model';
import { $ticketTmc } from '../ticket-tmc/ticket-tmc.model';

export const API_URL = process.env.API_URL;

export interface Gate {
  ticketId: number | string;
}

const entityTicket = {
  id: null,
  description: '',
  class: '',
  origin: '',
  status: '',
  types: [],
  formattedTypes: '-',
  priority: '',
  sla: '',
  assignees: '',
  planned_start_at: '',
  planned_end_at: '',
  object: '',
  house: '',
  apartment: '',
  is_appeal: false,
  is_escalation_enabled: false,
  property: '',
  initiator: '',
  until_repeat: '',
  type_repeat: '',
  every_repeat: '',
  actual_end_at: '',
  channel: '',
  enterprise: '',
  invoices: [],
  marketOrders: [],
  fields: {},
};

export const TicketGate = createGate<Gate>();

export const pageMounted = TicketGate.open;
export const pageUnmounted = TicketGate.close;

export const ticketSubmitted = createEvent<any>();
export const changedErrorDialogVisibility = createEvent<boolean>();
export const changedMode = createEvent<string>();
export const cloneTicket = createEvent<void>();
export const exportOrder = createEvent<void>();
// Для вызова с других страниц
export const needTicket = createEvent<any>();
export const exportTicket = createEvent<void>();
export const notifySupport = createEvent<number>();
export const checkPermissions = createEvent<void>();

export const fxGetTicketById = createEffect<TicketPayload, TicketResponse, Error>();
export const fxCreateTicket = createEffect<CreateTicketPayload, TicketResponse, Error>();
export const fxNotifySupport = createEffect<NotifySupportPayload, object, Error>();

export const $isLoading = createStore<boolean>(false);
export const $isLoadingTicket = createStore<boolean>(false);
export const $isLoadingCreateTicket = createStore(false);
export const $error = createStore<null | Error>(null);
export const $isErrorDialogOpen = createStore<boolean>(false);
export const $ticketData = createStore<Ticket>(entityTicket as any);
export const $mode = createStore<string>('view');

const formatObjects = (objects: ObjectsResponse[]) =>
  objects.reduce((acc: { [key: string]: ObjectParts[] }, item: ObjectsResponse) => {
    if (item.object.type === 'complex') {
      const type = { complex: item.parts };

      return { ...acc, ...type };
    }

    if (item.object.type === 'building') {
      const type = { building: item.parts };

      return { ...acc, ...type };
    }

    if (item.object.type === 'apartment') {
      const type = { apartment: item.parts };

      return { ...acc, ...type };
    }

    if (item.object.type === 'property') {
      const type = { property: item.parts };

      return { ...acc, ...type };
    }

    return acc;
  }, {});

export const $ticket = combine(
  $ticketData,
  $usersInfo,
  $types,
  $rates,
  $repeat,
  $ticketTmc,
  $objects,
  $enterprises,
  $invoices,
  $marketOrders,
  (
    ticketData,
    userInfo,
    types,
    rates,
    repeat,
    ticketTmc,
    objects,
    enterprises,
    invoices,
    marketOrders
  ) => {
    const currentType: any = ticketData.types[0];

    const typesList = currentType ? getTypesList(currentType, types) : [];

    const formattedObjects = formatObjects(objects);

    if (ticketData.class === '') {
      ticketData.object = '';
      ticketData.house = '';
      ticketData.property = '';
      ticketData.apartment = '';
    }

    if (formattedObjects.property) {
      ticketData.object = formattedObjects.property[0];
      ticketData.house = formattedObjects.property[1]?.id as any;
      ticketData.property = formattedObjects.property[2];
    }

    if (formattedObjects.apartment) {
      ticketData.object = formattedObjects.apartment[0];
      ticketData.house = formattedObjects.apartment[1]?.id as any;
      ticketData.apartment = formattedObjects.apartment[2];
    }

    if (formattedObjects.building) {
      ticketData.object = formattedObjects.building[0];
      ticketData.house = formattedObjects.building[1]?.id as any;
    }

    if (formattedObjects.complex) {
      ticketData.object = formattedObjects.complex[0];
    }

    const planned_duration = types.filter((item: Value) => item.id === currentType)[0]
      ?.planned_duration_minutes;

    if (Array.isArray(rates) && rates.length > 0) {
      const [rating] = rates;
      ticketData.rate_value = rating.rate;
      ticketData.rate_comment = rating.comment;
    }

    if (Array.isArray(userInfo) && userInfo.length > 0) {
      ticketData.assignees = userInfo;
    }

    if (typesList[0]) {
      const typesCount = typesList.length - 1;

      const types = typesList.map((item: Value, index: number) =>
        index !== typesCount ? `${item.title} -> ` : item.title
      );

      const formattedTypes = types.join('');

      ticketData.types = typesList;
      ticketData.formattedTypes = formattedTypes;
    }

    if (enterprises.length > 0) {
      const formattedEnterprises = enterprises.reduce(
        (acc: { [key: number]: Value }, value: Value) => ({
          ...acc,
          ...{ [value.id]: value },
        }),
        {}
      );

      const enterprise = formattedEnterprises[(ticketData as any).enterprise];

      if (enterprise) {
        ticketData.enterprise = enterprise;
      }
    }

    if (ticketTmc.length > 0) {
      ticketData.tmc = ticketTmc;
    } else {
      ticketData.tmc = [];
    }

    if (invoices.length > 0) {
      ticketData.invoices = invoices as any;
    } else {
      ticketData.invoices = [];
    }

    ticketData.marketOrders = marketOrders;

    ticketData.planned_duration = planned_duration;
    ticketData.type_repeat = (repeat as RepeatStore).type_repeat;
    ticketData.every_repeat = (repeat as RepeatStore).every_repeat;
    ticketData.until_repeat = (repeat as RepeatStore).until_repeat;
    ticketData.is_repeat_ticket = Boolean((repeat as RepeatStore).every_repeat);

    ticketData.rate_is_initially_rated =
      !!ticketData.rate_value || !!ticketData.rate_comment;

    return ticketData;
  }
);

export const entityApi = split(
  sample($ticket, ticketSubmitted, (ticket, payload) => ({
    ...ticket,
    ...payload,
  })),
  {
    create: ({ id }) => !id,
    update: ({ id }) => Boolean(id),
  }
);
